import { API_URL } from "./utils";

const backendUrl = process.env.REACT_APP_BACKEND_URL || API_URL;

export const DEFAULT_EXERCISE_CATEGORIES = [
	{ value: "strength", label: "Forza", labelKey: "strength" },
	{ value: "cardio", label: "Cardio", labelKey: "cardio" },
	{ value: "gymnastics", label: "Ginnastica", labelKey: "gymnastics" },
	{ value: "olympic", label: "Olimpici", labelKey: "olympic" },
	{ value: "accessory", label: "Accessori", labelKey: "accessory" },
	{ value: "conditioning", label: "Conditioning", labelKey: "exTypeConditioning" },
];

export const emptyConditioningTemplate = () => ({
	name: "",
	conditioning_type: "",
	time_cap_seconds: null,
	rounds: null,
	interval_seconds: null,
	conditioning_exercises: [],
	conditioning_free_text: "",
	emom_rounds: [],
	emom_conditioning_interval: "",
	notes: "",
});

export const isConditioningCategory = (value) =>
	typeof value === "string" && value.trim().toLowerCase() === "conditioning";

export const normalizeCategoryValue = (value) =>
	typeof value === "string" ? value.trim().toLowerCase() : value;

// Endurance/ergometer work is prescribed in calories (or meters), not reps or load.
export const isEnduranceCategory = (category) =>
	normalizeCategoryValue(category) === "endurance";

// Quantity field placeholder. Only endurance keeps the Uomo/Donna split (es. "20/16");
// everything else is a single reps value (the load field carries the Uomo/Donna split).
export const getConditioningQuantityPlaceholder = (category) =>
	isEnduranceCategory(category) ? "Cal/m Uomo/Donna" : "Reps";

/**
 * Minuscolo e **senza accenti**: "Rubén" e "ruben" devono essere la stessa cosa.
 *
 * Cercare il proprio atleta e non trovarlo perche' il nome ha un accento e'
 * esattamente il tipo di difetto che non sembra un difetto: sembra che l'atleta
 * non ci sia. Succedeva anche ai tool dell'assistente, dove costava di piu'
 * (`backend/services/text.py`).
 */
const foldForSearch = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

/**
 * Con chi e' condiviso un programma o una cartella, escluso il proprietario.
 *
 * Il backend fonde `coach_id` dentro `coach_ids` (_attach_program_access_metadata),
 * quindi `assigned_coaches` contiene **sempre** almeno il proprietario: senza
 * toglierlo risulterebbe condiviso tutto, e l'indicatore non direbbe niente.
 */
export const shareTargets = (item) => {
	const ownerId = item?.coach_id;
	const rawCoaches = (item?.assigned_coaches || []).length
		? item.assigned_coaches
		: (item?.coach_ids || []).map((id) => ({ user_id: id, name: id }));
	const rawTeams = (item?.assigned_coach_teams || []).length
		? item.assigned_coach_teams
		: (item?.coach_team_ids || []).map((id) => ({ team_id: id, name: id }));

	const coaches = rawCoaches.filter(
		(coach) => coach?.user_id && coach.user_id !== ownerId,
	);
	const teams = rawTeams.filter((team) => team?.team_id);
	return { coaches, teams, count: coaches.length + teams.length };
};

export const matchesCoachAthleteSearch = (athlete, query) => {
	const normalizedQuery = foldForSearch(query).trim();
	if (!normalizedQuery) return true;
	// `surname` esiste dai profili creati dopo il lancio: cercare per cognome
	// deve funzionare quanto cercare per nome.
	return [athlete?.name, athlete?.surname, athlete?.email].some((value) =>
		foldForSearch(value).includes(normalizedQuery),
	);
};

export const normalizeConditioningRounds = (value) => {
	if (value === null || value === undefined) return null;
	const textValue = String(value).trim();
	if (!textValue) return null;
	return /^\d+$/.test(textValue) ? Number(textValue) : textValue;
};

export const getCategoryColor = (category) => {
	const normalized =
		typeof category === "string" ? category.trim().toLowerCase() : category;
	const colors = {
		strength: "bg-volt/20 text-volt",
		cardio: "bg-blaze/20 text-blaze",
		gymnastics: "bg-acid/20 text-acid",
		olympic: "bg-purple-500/20 text-purple-400",
		accessory: "bg-gray-500/20 text-gray-400",
		conditioning: "bg-yellow-500/20 text-yellow-400",
	};
	return colors[normalized] || colors.accessory;
};

export const getImageUrl = (imageUrl) => {
	if (!imageUrl) return null;
	const raw = String(imageUrl).trim();
	if (!raw) return null;
	if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
	if (raw.startsWith("data:") || raw.startsWith("blob:")) return raw;
	if (/^av_[a-z0-9]+$/i.test(raw)) {
		return `${backendUrl}/api/users/picture/${raw}`;
	}
	if (raw.startsWith("/api/")) return `${backendUrl}${raw}`;
	if (raw.startsWith("api/")) return `${backendUrl}/${raw}`;
	if (raw.startsWith("/users/picture/")) return `${backendUrl}/api${raw}`;
	if (raw.startsWith("users/picture/")) return `${backendUrl}/api/${raw}`;
	if (raw.startsWith("/uploads/")) return `${backendUrl}/api${raw}`;
	if (raw.startsWith("/")) return `${backendUrl}${raw}`;
	return `${backendUrl}/${raw}`;
};

const YOUTUBE_VIDEO_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

export const getYouTubeVideoId = (value) => {
	const rawUrl = typeof value === "string" ? value.trim() : "";
	if (!rawUrl) return null;

	try {
		const parsedUrl = new URL(
			/^(?:https?:)?\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`,
		);
		const hostname = parsedUrl.hostname
			.toLowerCase()
			.replace(/^(?:www\.|m\.|music\.)/, "");
		let videoId = null;

		if (hostname === "youtu.be") {
			videoId = parsedUrl.pathname.split("/").filter(Boolean)[0];
		} else if (
			hostname === "youtube.com" ||
			hostname === "youtube-nocookie.com"
		) {
			videoId = parsedUrl.searchParams.get("v");
			if (!videoId) {
				const [route, routeVideoId] = parsedUrl.pathname
					.split("/")
					.filter(Boolean);
				if (["embed", "live", "shorts", "v"].includes(route)) {
					videoId = routeVideoId;
				}
			}
		}

		return YOUTUBE_VIDEO_ID_PATTERN.test(videoId || "") ? videoId : null;
	} catch {
		return null;
	}
};

export const getYouTubeThumbnail = (url) => {
	const videoId = getYouTubeVideoId(url);
	return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
};

export const getYouTubeEmbedUrl = (url) => {
	const videoId = getYouTubeVideoId(url);
	return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

/**
 * Smart search ranking: exact match first, then starts-with, then contains.
 * Each group is sorted alphabetically.
 */
export const rankAndFilterExercises = (list, query, nameField = "name") => {
	if (!query) return list;
	const q = query.toLowerCase().trim();
	const exact = [];
	const startsWith = [];
	const contains = [];
	for (const item of list) {
		const name = (item[nameField] || "").toLowerCase();
		if (name === q) exact.push(item);
		else if (name.startsWith(q)) startsWith.push(item);
		else if (name.includes(q)) contains.push(item);
	}
	const cmp = (a, b) => (a[nameField] || "").localeCompare(b[nameField] || "");
	startsWith.sort(cmp);
	contains.sort(cmp);
	return [...exact, ...startsWith, ...contains];
};

const normalizeExerciseName = (name) =>
	(name || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

/**
 * Collapses personal + global variants of the same exercise into a single entry
 * so every exercise picker shows one row per exercise, flagging its library scope.
 * Grouping priority: explicit source link (personal ↔ global) first, then same
 * normalized name across scopes, then exercise id. Personal variants are preferred
 * as the representative so selecting an entry keeps the coach's own exercise id.
 * Adds `has_personal_scope` / `has_global_scope` / `linked_global_exercise_id`.
 */
export const dedupeExercisesByScope = (exercises) => {
	const availableExercises = Array.isArray(exercises) ? exercises : [];
	const groups = new Map();
	const globalSourceIds = new Set(
		availableExercises
			.filter(
				(item) => item?.library_scope === "global" && item?.source_exercise_id,
			)
			.map((item) => String(item.source_exercise_id)),
	);

	const getCanonicalKey = (exercise) => {
		if (exercise?.library_scope === "global" && exercise?.source_exercise_id) {
			return `src:${String(exercise.source_exercise_id)}`;
		}
		if (
			exercise?.library_scope === "personal" &&
			exercise?.exercise_id &&
			globalSourceIds.has(String(exercise.exercise_id))
		) {
			return `src:${String(exercise.exercise_id)}`;
		}
		const normalized = normalizeExerciseName(exercise?.name);
		if (normalized) return `name:${normalized}`;
		return `id:${String(exercise?.exercise_id)}`;
	};

	availableExercises.forEach((exercise) => {
		if (!exercise?.exercise_id) return;
		const canonicalKey = getCanonicalKey(exercise);
		const existingGroup = groups.get(canonicalKey) || {
			personal: null,
			globals: [],
			items: [],
		};
		existingGroup.items.push(exercise);
		if (exercise.library_scope === "personal" && !existingGroup.personal) {
			existingGroup.personal = exercise;
		}
		if (exercise.library_scope === "global") {
			existingGroup.globals.push(exercise);
		}
		groups.set(canonicalKey, existingGroup);
	});

	const deduped = [];
	groups.forEach((group) => {
		const personalExercise = group.personal;
		const globalVariants = group.globals;

		if (personalExercise && globalVariants.length > 0) {
			deduped.push({
				...personalExercise,
				has_personal_scope: true,
				has_global_scope: true,
				linked_global_exercise_id: globalVariants[0].exercise_id,
			});
			return;
		}

		if (personalExercise) {
			deduped.push({
				...personalExercise,
				has_personal_scope: true,
				has_global_scope: false,
			});
			return;
		}

		if (globalVariants.length > 0) {
			const primaryGlobalExercise =
				globalVariants.find((item) => !item.source_exercise_id) ||
				globalVariants[0];
			deduped.push({
				...primaryGlobalExercise,
				has_personal_scope: false,
				has_global_scope: true,
			});
			return;
		}

		if (group.items.length > 0) {
			const fallbackExercise = group.items[0];
			deduped.push({
				...fallbackExercise,
				has_personal_scope: fallbackExercise.library_scope === "personal",
				has_global_scope: fallbackExercise.library_scope === "global",
			});
		}
	});

	return deduped;
};

/**
 * Single source of truth for exercise library-scope badges across every picker.
 * Returns "P/G" when the exercise lives in both personal and global libraries,
 * otherwise the localized single-scope label. Pass the `t` translator to localize.
 */
export const getExerciseScopeBadgeMeta = (exercise, t) => {
	const hasPersonal =
		exercise?.has_personal_scope === true ||
		exercise?.library_scope === "personal";
	const hasGlobal =
		exercise?.has_global_scope === true || exercise?.library_scope === "global";

	if (hasPersonal && hasGlobal) {
		return {
			className:
				"bg-volt/20 text-volt border border-volt/40 min-w-[34px] text-center",
			label: "P/G",
		};
	}

	if (hasGlobal) {
		return {
			className: "bg-amber-500/20 text-amber-300",
			label: t ? t("libraryScopeGlobalSingle") : "Globale",
		};
	}

	return {
		className: "bg-emerald-500/20 text-emerald-300",
		label: t ? t("libraryScopePersonal") : "Personale",
	};
};

/**
 * Returns input element properties based on performance profile unit type.
 * Ensures consistent formatting and constraints for different measurement types.
 * @param {string} unit - The unit type (kg, reps, seconds, minutes, minutes_seconds, meters, points)
 * @returns {object} - Properties for Input component (type, step, placeholder, pattern, title)
 */
export const RESULT_UNIT_TYPES = [
	{ value: "kg", label: "Peso (kg)", labelKey: "resultUnitKg" },
	{ value: "reps", label: "Ripetizioni", labelKey: "resultUnitReps" },
	{ value: "seconds", label: "Secondi", labelKey: "resultUnitSeconds" },
	{ value: "minutes", label: "Minuti", labelKey: "resultUnitMinutes" },
	{
		value: "minutes_seconds",
		label: "MM:SS",
		labelKey: "resultUnitMinutesSeconds",
	},
	{
		value: "time_or_reps",
		label: "Time or Reps (mm:ss o reps)",
		labelKey: "resultUnitTimeOrReps",
	},
	{ value: "meters", label: "Metri", labelKey: "resultUnitMeters" },
	{ value: "points", label: "Punti", labelKey: "resultUnitPoints" },
	// Punti / tempo: è un rapporto, quindi si mostra con due decimali e non si
	// somma. Più alto è meglio, come i punti.
	{ value: "hit_factor", label: "Hit factor", labelKey: "resultUnitHitFactor" },
];

const HIGHER_BETTER_UNITS = new Set([
	"kg",
	"reps",
	"meters",
	"points",
	"hit_factor",
]);

export const isHigherBetterForUnit = (unit) =>
	HIGHER_BETTER_UNITS.has(
		String(unit || "")
			.trim()
			.toLowerCase(),
	);

export const getInputPropsForUnit = (unit) => {
	const baseProps = {
		type: "number",
		step: "any",
		placeholder: "valore",
	};

	const unitProps = {
		kg: { type: "number", step: "0.01", placeholder: "kg" },
		reps: { type: "number", step: "1", placeholder: "reps" },
		seconds: { type: "number", step: "1", placeholder: "secondi" },
		minutes: { type: "number", step: "0.01", placeholder: "minuti" },
		minutes_seconds: {
			type: "text",
			step: null,
			placeholder: "MM:SS (es. 1:30)",
			title: "Formato: MM:SS o numero di secondi",
		},
		time_or_reps: {
			type: "text",
			step: null,
			placeholder: "mm:ss oppure reps totali",
			title:
				"Inserisci il tempo (mm:ss) se hai completato, oppure le reps totali se sei andato in timecap",
		},
		meters: { type: "number", step: "0.01", placeholder: "metri" },
		points: { type: "number", step: "1", placeholder: "punti" },
	};

	return { ...baseProps, ...(unitProps[unit] || {}) };
};

/**
 * Parses user input value to numeric format based on unit type.
 * Handles MM:SS format for minutes_seconds, otherwise expects numeric input.
 * @param {string|number} rawValue - The raw input value
 * @param {string} unit - The unit type
 * @returns {number|null} - Parsed numeric value or null if invalid
 */
export const parseValueByUnit = (rawValue, unit) => {
	if (rawValue === "" || rawValue === null || rawValue === undefined)
		return null;

	const parseLooseMinutesSeconds = (value) => {
		const text = String(value || "").trim();
		const match = text.match(/^(\d{1,3})\s*:\s*(\d{1,3})$/);
		if (!match) return null;
		const minutes = Number(match[1]);
		const seconds = Number(match[2]);
		if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
		if (minutes < 0 || seconds < 0) return null;
		return minutes * 60 + seconds;
	};

	if (unit === "minutes_seconds") {
		// Try to parse MM:SS format first
		const parsedSeconds = parseLooseMinutesSeconds(rawValue);
		if (parsedSeconds !== null) return parsedSeconds;

		// If not MM:SS format, try parsing as plain number (seconds)
		const num = Number(rawValue);
		if (!Number.isFinite(num)) return null;
		return num >= 0 ? num : null;
	}

	if (unit === "time_or_reps") {
		const parsedSeconds = parseLooseMinutesSeconds(rawValue);
		if (parsedSeconds !== null) return parsedSeconds;
		const num = Number(rawValue);
		if (!Number.isFinite(num)) return null;
		return num >= 0 ? num : null;
	}

	const num = Number(rawValue);
	if (!Number.isFinite(num)) return null;
	return num;
};

export const normalizeTimeOrRepsInput = (rawValue) => {
	if (rawValue === "" || rawValue === null || rawValue === undefined) return "";
	const text = String(rawValue).trim();
	if (!text.includes(":")) return text;
	const match = text.match(/^(\d{1,3})\s*:\s*(\d{1,3})$/);
	if (!match) return text;
	const minutes = Number(match[1]);
	const seconds = Number(match[2]);
	if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return text;
	if (minutes < 0 || seconds < 0) return text;
	const totalSeconds = minutes * 60 + seconds;
	const normalizedMinutes = Math.floor(totalSeconds / 60);
	const normalizedSeconds = totalSeconds % 60;
	return `${normalizedMinutes}:${String(normalizedSeconds).padStart(2, "0")}`;
};

/**
 * Formats numeric value to display format based on unit type.
 * Converts seconds to MM:SS for minutes_seconds, otherwise returns as-is.
 * @param {number} value - The numeric value to format
 * @param {string} unit - The unit type
 * @returns {string} - Formatted value for display
 */
export const formatValueByUnit = (value, unit) => {
	if (value === null || value === undefined || value === "") return "";

	if (unit === "minutes_seconds") {
		const total = Number(value);
		if (!Number.isFinite(total) || total < 0) return "";
		const minutes = Math.floor(total / 60);
		const seconds = Math.floor(total % 60);
		return `${minutes}:${String(seconds).padStart(2, "0")}`;
	}

	// time_or_reps is stored as raw text (mm:ss or reps integer) — return as-is
	if (unit === "time_or_reps") return String(value);

	// L'hit factor è un rapporto: senza i decimali 8.01 e 8.49 diventerebbero
	// lo stesso numero, e su un drill sono due prestazioni distinte.
	if (unit === "hit_factor") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed.toFixed(2) : String(value);
	}

	return String(value);
};
